import { IconButton, Tooltip } from '@mui/joy';
import { DownloadSimple } from '@phosphor-icons/react';
import { useIntl } from 'react-intl';

import { ViewAttachmentModal } from './Attachment';

import { Text } from '~/components/ui/text';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ReportService } from '~/services/api/reports/endpoints';
import { useWrappedMutation } from '~/services/api/rest-api';
import { UserService } from '~/services/api/users/endpoints';

export interface FileMessageAttachment {
  id: number;
  original_file_name: string;
  attachment_type: string;
  file_type: string;
  url: string;
  upload_finished_at: string;
  uploaded_by: string;
  user_full_name: string;
  user_type: number;
}

interface MessageAttachmentsProps {
  attachments: FileMessageAttachment[];
  reportId: string;
  isReporter?: boolean;
}

const MessageAttachments = ({
  attachments,
  reportId,
  isReporter = false,
}: Readonly<MessageAttachmentsProps>) => {
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const downloadReportFile = useWrappedMutation(
    ReportService.endpoints.downloadReportFile.useMutation(),
  );

  const flagsQuery = !isReporter
    ? UserService.endpoints.flags.useQuery()
    : null;

  const downloadAttachment = async (url: string, name: string) => {
    if (flagsQuery?.data?.is_allowed_to_download_attachments || isReporter) {
      downloadReportFile.mutateAsync(
        {
          file: {
            url: url,
            original_file_name: name,
          },
          report_reference: reportId,
          slug: slug,
          no_audit: true,
        },
        {
          onSuccess: (data) => {
            const fileUrl = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', name);
            document.body.appendChild(link);
            link.click();
          },
        },
      );
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'You are not allowed to download attachments',
        }),
        'error',
      );
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '10px',
        width: '100%',
        padding: '10px 0px',
      }}
    >
      {attachments?.map((file, key) => {
        return (
          <div
            key={`${file.id}-${key}`}
            className='flex flex-row items-center gap-2 border-2 border-slate-300 rounded-md px-4 py-2 w-72 min-w-72'
          >
            <ViewAttachmentModal
              file={file}
              downloadAttachment={() =>
                downloadAttachment(file.url, file.original_file_name)
              }
            >
              <div className='w-10 h-10 cursor-pointer'>
                <img
                  src={file.url}
                  alt={file.original_file_name}
                  className='w-full h-full object-cover'
                />
              </div>
            </ViewAttachmentModal>
            <div>
              <Tooltip title={file?.original_file_name}>
                <Text
                  level='body-md'
                  className='text-ellipsis whitespace-nowrap overflow-hidden w-40'
                >
                  {file.original_file_name}
                </Text>
              </Tooltip>
              <Tooltip title={file?.file_type}>
                <Text
                  level='body-md'
                  className='text-gray-600 text-ellipsis whitespace-nowrap overflow-hidden w-40 uppercase'
                >
                  {file?.file_type}
                </Text>
              </Tooltip>
            </div>
            <IconButton
              disabled={downloadReportFile.state.isLoading}
              onClick={() =>
                downloadAttachment(file.url, file.original_file_name)
              }
            >
              <DownloadSimple />
            </IconButton>
          </div>
        );
      })}
    </div>
  );
};

export default MessageAttachments;
